/**
 * OperatorChatPanel
 *
 * Narrow, collapsible conversation thread wrapper for the operator page.
 * The thread is secondary to the canvas — it starts at ~350px and can
 * be collapsed entirely. When collapsed, animates to zero width. The parent
 * renders a CollapsedPanelBar chip instead.
 */

import { useState, useCallback, type ReactNode } from 'react';
import { PanelLeftClose, Plus } from 'lucide-react';
import type { AssistantRuntime } from '@assistant-ui/react';
import { ConversationThread } from '../../components/assistant-ui/conversation-thread';

const DEFAULT_WIDTH = 600;
const MIN_WIDTH = 400;
const MAX_WIDTH = 800;

const clampWidth = (w: number) => Math.max(MIN_WIDTH, Math.min(w, MAX_WIDTH));

interface OperatorChatPanelProps {
  runtime: AssistantRuntime;
  agentId: string;
  /** Bridge ref from `useAgentRuntime` for the composer's attachments. */
  consumeAttachmentsRef?: React.MutableRefObject<
    (() => Promise<import('../../types').FileRef[]>) | undefined
  >;
  collapsed: boolean;
  onToggle: () => void;
  onNewChat?: () => void;
  header?: ReactNode;
  headerActions?: ReactNode;
  initialWidth?: number;
  onWidthCommit?: (width: number) => void;
}

const DEFAULT_HEADER = (
  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
    Chat
  </h3>
);

export default function OperatorChatPanel({
  runtime,
  agentId,
  consumeAttachmentsRef,
  collapsed,
  onToggle,
  onNewChat,
  header = DEFAULT_HEADER,
  headerActions,
  initialWidth,
  onWidthCommit,
}: OperatorChatPanelProps) {
  const [panelWidth, setPanelWidth] = useState(() =>
    clampWidth(initialWidth ?? DEFAULT_WIDTH),
  );
  // Drives whether the width transition is active. The 300ms ease is wanted for
  // the collapse/expand toggle, but during an active drag it makes the outer
  // container lag behind the pointer while the inner content (width: panelWidth)
  // jumps instantly — the content then overflows the lagging container and
  // clips. So we turn the transition OFF while dragging and back on after.
  const [dragging, setDragging] = useState(false);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const startX = e.clientX;
    const startWidth = panelWidth;
    let lastWidth = startWidth;

    const onMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      lastWidth = clampWidth(startWidth + delta);
      setPanelWidth(lastWidth);
    };
    const onMouseUp = () => {
      setDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (lastWidth !== startWidth) {
        onWidthCommit?.(lastWidth);
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [panelWidth, onWidthCommit]);

  return (
    <div
      className={`shrink-0 overflow-hidden border-r border-border bg-background ${
        dragging ? '' : 'transition-[width] duration-300 ease-in-out'
      }`}
      style={{ width: collapsed ? 0 : panelWidth, borderRightWidth: collapsed ? 0 : 1 }}
    >
      <div
        className="relative flex h-full flex-col"
        style={{ width: panelWidth }}
      >
        {/* Drag handle on right edge */}
        <div
          className="absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize hover:bg-primary/30 active:bg-primary/50"
          onMouseDown={handleDragStart}
        />

        {/* Header with collapse toggle */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-3">
          {header}
          <div className="flex items-center gap-1">
            {onNewChat && (
              <button
                type="button"
                onClick={onNewChat}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                title="New conversation"
              >
                <Plus className="h-3 w-3" />
                New
              </button>
            )}
            {headerActions}
            <button
              type="button"
              onClick={onToggle}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Collapse chat"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Conversation thread */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <ConversationThread runtime={runtime} agentId={agentId} consumeAttachmentsRef={consumeAttachmentsRef} />
        </div>
      </div>
    </div>
  );
}
